import { 
  Home, 
  Users, 
  Shield, 
  FileText, 
  FileSignature,
  UserCog, 
  LogOut,
  FileCheck,
  Wrench,
  Building2,
  ClipboardList,
  Calendar,
  AlertTriangle,
  Code,
  LayoutGrid,
  CalendarClock,
  Inbox,
  Lightbulb,
  Package,
  Plane,
  Factory,
  Radio
} from "lucide-react";
import { SugerenciaDialog } from "@/components/SugerenciaDialog";
import { NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import ravenLogo from "@/assets/raven-logo.png";
import { getImageUrl } from "@/lib/imageUtils";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const getIconColor = (title: string) => {
  const colorMap: Record<string, string> = {
    "Dashboard": "text-[hsl(var(--raven-ice))]",
    "Gestión de Empresas": "text-[hsl(var(--raven-violet))]",
    "Ver Clientes": "text-[hsl(var(--raven-violet))]",
    "Clientes con Abonado": "text-red-400",
    "Partes de Trabajo": "text-cyan-400",
    "Gestión de Obras": "text-orange-400",
    "Mandato SEPA": "text-blue-400",
    
    "Mi Calendario": "text-cyan-400",
    "Gestión de OTs": "text-purple-400",
  };
  return colorMap[title] || "text-[hsl(var(--raven-ice))]";
};

const getMainItems = (userRole: string) => {
  // Super admin items
  if (userRole === "super_admin") {
    return [
      { title: "Gestión de Empresas", url: "/super-admin/empresas", icon: Building2 },
      { title: "Catálogo Global", url: "/super-admin/catalogo-global", icon: Factory },
      { title: "Buzón de Feedback", url: "/admin/feedback", icon: Inbox },
    ];
  }

  // Common items for all authenticated users
  const commonItems = [
    { title: "Dashboard", url: "/", icon: Home },
    { title: "Ver Clientes", url: "/clientes", icon: Users },
    { title: "Clientes con Abonado", url: "/clientes-abonados", icon: Radio },
    { title: "Partes de Trabajo", url: "/partes-trabajo", icon: FileText },
    { title: "Recepción Material", url: "/recepciones", icon: Package },
  ];

  // Items for technicians (field work)
  const tecnicoItems = [
    { title: "Mi Calendario", url: "/mi-calendario", icon: Calendar },
    { title: "Mis Ausencias", url: "/mis-ausencias", icon: Plane },
  ];

  // Items for gestors (office work, can manage OTs)
  const gestorItems = [
    { title: "Gestión de OTs", url: "/asignacion-tareas", icon: LayoutGrid },
    { title: "Mantenimientos", url: "/mantenimientos", icon: Wrench },
    { title: "Bandeja Avisos", url: "/avisos", icon: Inbox },
    { title: "Jornadas Equipo", url: "/jornadas-equipo", icon: CalendarClock },
    { title: "Ausencias Equipo", url: "/ausencias-equipo", icon: Plane },
  ];

  // Obras: visible para todos los roles operativos
  const obrasItems = [
    { title: "Seguimiento de Obras", url: "/obras", icon: Building2 },
  ];

  // Items only for admin
  const adminOnlyItems = [
    { title: "Mandato SEPA", url: "/mandato-sepa", icon: FileSignature },
  ];

  switch (userRole) {
    case 'admin':
      return [...commonItems, ...tecnicoItems, ...gestorItems, ...obrasItems, ...adminOnlyItems];
    case 'gestor':
      return [...commonItems, ...gestorItems, ...obrasItems];
    case 'tecnico':
      return [...commonItems, ...tecnicoItems];
    default:
      return commonItems;
  }
};

const getAdminIconColor = (title: string) => {
  const colorMap: Record<string, string> = {
    "Jornadas Equipo": "text-orange-400",
    "Gestión Usuarios": "text-[hsl(var(--raven-violet))]",
    "Códigos de Anomalías": "text-yellow-400",
    "Catálogo de Equipos": "text-amber-400",
    "Herramientas": "text-green-400",
  };
  return colorMap[title] || "text-purple-400";
};

const adminItems = [
  { title: "Gestión Usuarios", url: "/gestion-usuarios", icon: UserCog },
  { title: "Códigos de Anomalías", url: "/codigos-anomalias", icon: Code },
  { title: "Catálogo de Equipos", url: "/catalogo-equipos", icon: Wrench },
  { title: "Herramientas", url: "/herramientas", icon: Wrench },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const { perfil, signOut, empresa } = useAuth();
  const collapsed = state === "collapsed";
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  
  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const getNavClass = (isActiveRoute: boolean) =>
    isActiveRoute 
      ? "sidebar-item-active font-medium" 
      : "sidebar-item-hover";

  const handleLogout = async () => {
    await signOut();
  };

  const userRole = perfil?.rol || "";
  const showAdminSection = userRole === "admin";

  return (
    <Sidebar className={`${collapsed ? "w-14" : "w-64"} sidebar-background border-r border-border/50`} collapsible="icon">
      {/* Header with Logo */}
      {!collapsed && (
        <div className="sidebar-header p-4 mb-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="sidebar-logo-glow">
                <img src={ravenLogo} alt="Raven" className="h-10 w-10 object-contain" />
              </div>
              {empresa?.logo_url && perfil?.rol !== 'super_admin' && (
                <div className="relative h-10 rounded-md overflow-hidden flex items-center justify-center">
                  <img 
                    src={getImageUrl(empresa.logo_url) || ""} 
                    alt={empresa.nombre} 
                    className="h-10 max-w-[120px] object-contain"
                  />
                </div>
              )}
            </div>
            <div>
              <h2 className="sidebar-title-gradient text-xl font-display uppercase tracking-wider">Raven</h2>
              {empresa && perfil?.rol !== 'super_admin' && (
                <Badge variant="outline" className="mt-1 text-xs border-[hsl(var(--raven-ice))] text-[hsl(var(--raven-ice))]">
                  {empresa.nombre}
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}
      {collapsed && (
        <div className="flex justify-center py-4">
          {empresa?.logo_url && perfil?.rol !== 'super_admin' ? (
            <div className="relative h-8 rounded-md overflow-hidden flex items-center justify-center">
              <img 
                src={getImageUrl(empresa.logo_url) || ""} 
                alt={empresa.nombre} 
                className="h-8 max-w-[32px] object-contain"
              />
            </div>
          ) : (
            <div className="sidebar-logo-glow">
              <img src={ravenLogo} alt="Raven" className="h-8 w-8 object-contain" />
            </div>
          )}
        </div>
      )}
      
      <SidebarContent>
        <SidebarGroup>
          {!collapsed && (
            <SidebarGroupLabel className="sidebar-group-label px-4 py-2">
              Navegación Principal
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu>
              {getMainItems(userRole).map((item) => {
                const isActiveRoute = isActive(item.url);
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink 
                        to={item.url} 
                        end={item.url === "/"}
                        className={({ isActive }) => getNavClass(isActive)}
                      >
                        <item.icon 
                          className={`h-4 w-4 transition-all duration-300 ${getIconColor(item.title)} ${isActiveRoute ? 'sidebar-icon-pulse' : ''}`}
                        />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {showAdminSection && (
          <>
            <div className="sidebar-separator-gradient my-4" />
            <SidebarGroup>
              {!collapsed && (
                <SidebarGroupLabel className="sidebar-group-label px-4 py-2">
                  Administración
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {adminItems.map((item) => {
                    const isActiveRoute = isActive(item.url);
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild>
                          <NavLink 
                            to={item.url}
                            className={({ isActive }) => getNavClass(isActive)}
                          >
                            <item.icon 
                              className={`h-4 w-4 transition-all duration-300 ${getAdminIconColor(item.title)} ${isActiveRoute ? 'sidebar-icon-pulse' : ''}`}
                            />
                            {!collapsed && <span>{item.title}</span>}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        <div className="sidebar-separator-gradient my-4" />
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {userRole && userRole !== 'super_admin' && (
                <SidebarMenuItem>
                  <SugerenciaDialog
                    trigger={
                      <SidebarMenuButton className="sidebar-item-hover">
                        <Lightbulb className="h-4 w-4 text-yellow-400 transition-all duration-300" />
                        {!collapsed && <span>Sugerir mejora</span>}
                      </SidebarMenuButton>
                    }
                  />
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
                  <AlertDialogTrigger asChild>
                    <SidebarMenuButton className="sidebar-item-hover">
                      <LogOut className="h-4 w-4 text-red-400 transition-all duration-300" />
                      {!collapsed && <span>Cerrar Sesión</span>}
                    </SidebarMenuButton>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Cerrar sesión?</AlertDialogTitle>
                      <AlertDialogDescription>
                        ¿Está seguro que desea cerrar la sesión? Será redirigido a la página de inicio de sesión.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleLogout}>
                        Cerrar Sesión
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
